import { useEffect, useMemo, useState, useSyncExternalStore, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import { Check, Close, Search } from '../components/ui/icons';
import { LiveMark } from '../components/ui/LiveMark';
import { api, ApiError, type MemoryItem } from '../lib/api';
import { formatShortDate } from '../lib/format';
import { MemoryUndoStore } from '../lib/memory-undo';

type Group = 'preference' | 'routine' | 'person' | 'fact' | 'episodic';
const GROUPS: Group[] = ['preference', 'routine', 'person', 'fact', 'episodic'];

/**
 * You → Memory (What TWO knows): the person's active memories from GET /memories, grouped
 * Preferences / People / Facts / Recent, each with its source line, inline Edit and Forget
 * with a 5-second undo, and a search field. Rules and whitespace, no card per item.
 */
export function MemoryScreen() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  // M5.3: forget/undo live in a store; the row leaves only after the server said 2xx, Undo
  // stays for exactly 5 s in a toast that never shifts the list under the finger.
  const [store] = useState(
    () =>
      new MemoryUndoStore<MemoryItem>([], {
        forget: (id) => api.forgetMemory(id),
        restore: (id) => api.restoreMemory(id),
      }),
  );
  const snap = useSyncExternalStore(store.subscribe, store.getSnapshot);
  const [loaded, setLoaded] = useState(false);
  const items = loaded ? snap.items : null;
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .memories()
      .then((res) => {
        if (cancelled) return;
        store.setItems(res.memories);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [store]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (items ?? []).filter((m) => !q || m.content.toLowerCase().includes(q));
  }, [items, query]);

  const startEdit = (m: MemoryItem) => {
    setEditing(m.memory_id);
    setDraft(m.content);
    setError(null);
  };

  const saveEdit = async (e: FormEvent, m: MemoryItem) => {
    e.preventDefault();
    const content = draft.trim();
    if (!content || content === m.content) {
      setEditing(null);
      return;
    }
    setBusy(m.memory_id);
    try {
      const { memory } = await api.updateMemory(m.memory_id, content);
      store.setItems(snap.items.map((x) => (x.memory_id === m.memory_id ? memory : x)));
      setEditing(null);
    } catch (err) {
      setError(err instanceof ApiError ? t('common.error') : t('signin.errors.network'));
    } finally {
      setBusy(null);
    }
  };

  const forget = (m: MemoryItem) => {
    setError(null);
    void store.forget(m);
  };

  const restore = () => void store.undo();

  return (
    <div className="flex flex-col gap-4 p-4">
      <p className="text-sm text-ink-2">{t('memory.contextLine')}</p>
      <div className="control flex h-12 items-center gap-2 rounded-md border border-line-strong bg-surface px-3">
        <Search size={18} className="text-ink-3" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('memory.search')}
          aria-label={t('memory.search')}
          enterKeyHint="search"
          className="h-full min-w-0 flex-1 bg-transparent text-base"
          autoComplete="off"
        />
      </div>

      {snap.error ? (
        <p role="alert" className="text-sm text-critical-text" data-memory-error>
          {snap.error === 'not_forgotten' ? t('memory.notForgotten') : t('common.error')}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-critical-text">
          {error}
        </p>
      ) : null}

      {items === null ? (
        <div className="flex items-center gap-3 py-2" role="status">
          <LiveMark state="thinking" size={16} title={t('common.loading')} />
        </div>
      ) : items.length === 0 ? (
        <div
          role="status"
          className="rounded-lg border border-line bg-surface p-6 text-center text-sm text-ink-2 dark:bg-elevated"
        >
          {t('settings.empty.memory')}
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-ink-2">{t('memory.noMatch')}</p>
      ) : (
        GROUPS.map((group) => {
          const rows = filtered.filter((m) => m.type === group);
          if (rows.length === 0) return null;
          return (
            <section key={group} aria-label={t(`memory.groups.${group}`)}>
              <h3 className="mb-1 text-sm font-semibold text-ink-2">
                {t(`memory.groups.${group}`)}
              </h3>
              <ul className="divide-y divide-line">
                {rows.map((m) => (
                  <li key={m.memory_id} className="py-3" dir="auto" data-memory-row>
                    {editing === m.memory_id ? (
                      <form onSubmit={(e) => void saveEdit(e, m)} className="flex flex-col gap-2">
                        <Field
                          label={t('memory.edit')}
                          hideLabel
                          dir="auto"
                          value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          autoFocus
                          maxLength={500}
                          disabled={busy === m.memory_id}
                        />
                        <div className="flex gap-2">
                          <Button
                            type="submit"
                            size="md"
                            loading={busy === m.memory_id}
                            leading={<Check size={16} />}
                          >
                            {t('memory.save')}
                          </Button>
                          <Button
                            type="button"
                            size="md"
                            variant="ghost"
                            onClick={() => setEditing(null)}
                          >
                            {t('common.close')}
                          </Button>
                        </div>
                      </form>
                    ) : (
                      <>
                        <p className="text-base text-ink">{m.content}</p>
                        <p className="tabular mt-0.5 text-xs text-ink-3">
                          {m.source_event_id ? (
                            <Link
                              to={`/two#${m.source_event_id}`}
                              className="hover:text-ink hover:underline"
                            >
                              {t('memory.toldOn', {
                                date: formatShortDate(new Date(m.created_at), locale),
                              })}
                            </Link>
                          ) : (
                            t('memory.toldOn', {
                              date: formatShortDate(new Date(m.created_at), locale),
                            })
                          )}
                        </p>
                        <div className="mt-1 flex gap-1">
                          <button
                            type="button"
                            onClick={() => startEdit(m)}
                            className="-ms-2 min-h-9 rounded-md px-2 text-sm font-medium text-brand hover:bg-brand-soft"
                          >
                            {t('memory.edit')}
                          </button>
                          <button
                            type="button"
                            onClick={() => forget(m)}
                            disabled={busy === m.memory_id || snap.busy === m.memory_id}
                            className="inline-flex min-h-9 items-center gap-1 rounded-md px-2 text-sm font-medium text-ink-2 hover:bg-canvas hover:text-ink"
                          >
                            <Close size={14} />
                            {t('memory.forget')}
                          </button>
                        </div>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          );
        })
      )}
      {snap.pending ? (
        <div
          role="status"
          data-undo-toast
          className="fixed inset-x-4 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-40 mx-auto flex max-w-md items-center gap-3 rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink shadow-lg md:bottom-6 dark:bg-elevated"
        >
          <span className="flex-1">{t('memory.forgotten')}</span>
          <button
            type="button"
            onClick={restore}
            className="min-h-9 rounded-md px-2 font-medium text-brand hover:underline"
          >
            {t('memory.undo')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
